import fb from 'firebase/compat/app';
import 'firebase/compat/firestore';
import 'firebase/compat/auth';
import { firebaseConfig } from '@/config/firebase-config';
import { CollectionReference, DocumentData } from '@firebase/firestore-types';
import { Bid, Material, Vendor, Client, User, AppDefaults, Truck, Model, UserSettings } from '@/types/database';
import { GlobalUser } from '@/types/shared';
import { CollectionNamespaces } from '@/enums';
import { getCreatedOnSignature, getUpdatedOnSignature, removeIdAndLastUpdatedOnFields } from '@/utils';
import { cloudFunctionEndpoints } from '@/constants';
import { IRepository } from '@/interfaces';

//* Uncomment for firestore logging
// if (process.env.NODE_ENV === 'development') {
//   fb.setLogLevel('debug');
// }

const firebase = fb.initializeApp(firebaseConfig);
const firestore = firebase.firestore();
const auth = firebase.auth();
auth.setPersistence(fb.auth.Auth.Persistence.LOCAL);

const ensureFirebaseInit = () =>
  new Promise<void>(resolve => {
    auth.onAuthStateChanged(user => {
      if (user === undefined) return;
      resolve();
    });
  });

const groupsCollection = firestore.collection(CollectionNamespaces.Groups);
const globalUsersCollection = firestore.collection(CollectionNamespaces.GlobalUsers);
const usersCollection = (groupId: string) => groupsCollection.doc(groupId).collection(CollectionNamespaces.Users);
const vendorsCollection = (groupId: string) => groupsCollection.doc(groupId).collection(CollectionNamespaces.Vendors);
const materialsCollection = (groupId: string) => groupsCollection.doc(groupId).collection(CollectionNamespaces.Materials);
const clientsCollection = (groupId: string) => groupsCollection.doc(groupId).collection(CollectionNamespaces.Clients);
const bidsCollection = (groupId: string) => groupsCollection.doc(groupId).collection(CollectionNamespaces.Bids);
const appDefaultsCollection = (groupId: string) =>
  groupsCollection.doc(groupId).collection(CollectionNamespaces.AppDefaults);
const trucksCollection = (groupId: string) => groupsCollection.doc(groupId).collection(CollectionNamespaces.Trucks);
const userSettingsCollection = (groupId: string) =>
  groupsCollection.doc(groupId).collection(CollectionNamespaces.UserSettings);

const createDocumentOnCollection = <T>(collection: CollectionReference<DocumentData>) => (payload: T) =>
  collection.add({
    ...removeIdAndLastUpdatedOnFields(payload),
    ...getCreatedOnSignature(auth.currentUser?.uid ?? ''),
    ...getUpdatedOnSignature(auth.currentUser?.uid ?? '')
  });
const updateDocumentOnCollection = <T>(collection: CollectionReference<DocumentData>) => (documentId: string) => (
  payload: Partial<T>
) =>
  collection.doc(documentId).update({
    ...removeIdAndLastUpdatedOnFields(payload),
    ...getUpdatedOnSignature(auth.currentUser?.uid ?? '')
  });
const deleteDocumentOnCollection = (collection: CollectionReference<DocumentData>) => (documentId: string) =>
  collection.doc(documentId).delete();

/**
 * Creates a Firestore snapshot listener that will call a given function when changes are seen and pass the updated documents with their ids appended.
 * @param collection A Firestore collection reference.
 * @param storeCallback A function to call when updates are made.
 * @returns A function to unsubscribe from the snapshot listener.
 */
const createListener = (collection: CollectionReference<DocumentData>) => (storeCallback: (payload: unknown) => void) =>
  collection.onSnapshot(snapshot => storeCallback(snapshot.docs.flatMap(doc => ({ id: doc.id, ...doc.data() }))));

const useOperators = <T extends Model>(collection: CollectionReference<DocumentData>): IRepository<T> => ({
  create: createDocumentOnCollection<Omit<T, 'id' | 'createdOn' | 'lastUpdatedOn'>>(collection),
  update: updateDocumentOnCollection<Omit<T, 'createdOn' | 'lastUpdatedOn'>>(collection),
  delete: deleteDocumentOnCollection(collection),
  listen: createListener(collection)
});

const useUserOperators = (groupId: string) => useOperators<User>(usersCollection(groupId));
const useVendorOperators = (groupId: string) => useOperators<Vendor>(vendorsCollection(groupId));
const useMaterialOperators = (groupId: string) => useOperators<Material>(materialsCollection(groupId));
const useClientOperators = (groupId: string) => useOperators<Client>(clientsCollection(groupId));
const useBidOperators = (groupId: string) => useOperators<Bid>(bidsCollection(groupId));
const useAppDefaultOperators = (groupId: string) => useOperators<AppDefaults>(appDefaultsCollection(groupId));
const useTruckOperators = (groupId: string) => useOperators<Truck>(trucksCollection(groupId));
const useUserSettingsOperators = (groupId: string) => useOperators<UserSettings>(userSettingsCollection(groupId));

const getUserGroupId = async () => {
  const user = await globalUsersCollection.doc(auth.currentUser?.uid).get();

  if (!user?.exists) return null;

  return (user?.data() as GlobalUser).groupId;
};

const getGroupLinkId = async () => {
  const groupId = await getUserGroupId();
  if (!groupId) return '';

  const groupRef = await groupsCollection.doc(groupId).get();

  return groupRef.data()?.linkId;
};

export const useFirestore = () => {
  return {
    getGroupLinkId,
    getUserGroupId,
    useUserOperators,
    useVendorOperators,
    useMaterialOperators,
    useClientOperators,
    useBidOperators,
    useAppDefaultOperators,
    useTruckOperators,
    useUserSettingsOperators
  };
};

export const ensureFirestoreAuth = (onEmptyAuthState: () => void): boolean => {
  if (!auth.currentUser) {
    onEmptyAuthState();
    return false;
  }

  return true;
};

export const useFirebaseAuth = () => ({
  auth,
  ensureFirebaseInit,
  ensureFirestoreAuth,
  providers: {
    GoogleAuthProvider: fb.auth.GoogleAuthProvider,
    EmailAuthProvider: fb.auth.EmailAuthProvider
  }
});

const createGroup = async (groupName: string, userIdToken: string) => {
  const url = new URL(cloudFunctionEndpoints.createGroup.url);

  if (cloudFunctionEndpoints.createGroup.queryParams) {
    url.searchParams.set(cloudFunctionEndpoints.createGroup.queryParams.groupName, groupName);
    url.searchParams.set(cloudFunctionEndpoints.createGroup.queryParams.userIdToken, userIdToken);
  }

  const response = await fetch(url.toString(), {
    mode: 'cors',
    headers: {
      'Content-Type': 'text/plain'
    }
  });

  if (response instanceof Error) return '';

  return response.clone().text();
};

const getGroupNameByLinkId = async (linkId: string) => {
  const url = new URL(cloudFunctionEndpoints.getGroupNameByLinkId.url);

  if (cloudFunctionEndpoints.getGroupNameByLinkId.queryParams) {
    url.searchParams.set(cloudFunctionEndpoints.getGroupNameByLinkId.queryParams.linkId, linkId);
  }

  const response = await fetch(url.toString(), {
    mode: 'cors',
    headers: {
      'Content-Type': 'text/plain'
    }
  });

  if (response instanceof Error) return '';

  return response.clone().text();
};

const joinWithGroupLink = async (userIdToken: string, linkId: string) => {
  const url = new URL(cloudFunctionEndpoints.joinWithGroupLink.url);

  if (cloudFunctionEndpoints.joinWithGroupLink.queryParams) {
    url.searchParams.set(cloudFunctionEndpoints.joinWithGroupLink.queryParams.userIdToken, userIdToken);
    url.searchParams.set(cloudFunctionEndpoints.joinWithGroupLink.queryParams.linkId, linkId);
  }

  const response = await fetch(url.toString(), {
    mode: 'cors'
  });

  return response.ok;
};

const createNewGroupLink = async (groupId: string) => {
  const url = new URL(cloudFunctionEndpoints.createNewGroupLink.url);

  if (cloudFunctionEndpoints.createNewGroupLink.queryParams) {
    url.searchParams.set(cloudFunctionEndpoints.createNewGroupLink.queryParams.groupId, groupId);
  }

  const response = await fetch(url.toString(), {
    mode: 'cors',
    headers: {
      'Content-Type': 'text/plain'
    }
  });

  return response.clone().text();
};

const removeGroup = async (email: string, name: string) => {
  const url = new URL(cloudFunctionEndpoints.removeGroupId.url);

  if (cloudFunctionEndpoints.removeGroupId.queryParams) {
    url.searchParams.set(cloudFunctionEndpoints.removeGroupId.queryParams.email, email);
    url.searchParams.set(cloudFunctionEndpoints.removeGroupId.queryParams.name, name);
  }

  const response = await fetch(url.toString(), {
    mode: 'cors',
    headers: {
      'Content-Type': 'text/plain'
    }
  });

  return response.clone().text();
};

export const useFirebaseCloudFunctions = () => ({
  createGroup,
  getGroupNameByLinkId,
  joinWithGroupLink,
  createNewGroupLink,
  removeGroup
});
